import { useEffect, useState } from 'react';
import { fetchRegistrantReferral, type RegistrantReferral } from '../api';

/**
 * "Bring a colleague": the registrant's own referral link and how many
 * registered through it, on the event thank-you page (sop-ee-referrals,
 * MC job mct_5adfdc52-17d). Sits below the prep-survey card so the survey
 * stays the one thing above the fold (docs/plans/summit-upgrade-step-and-survey-thank-you.md).
 *
 * Renders nothing until the worker answers, and nothing at all when the event
 * has no live referral campaign giving registrants links.
 */
export function ReferralBlock({ submissionId }: { submissionId?: string }) {
  const [referral, setReferral] = useState<RegistrantReferral | null>(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!submissionId) return;
    let alive = true;
    fetchRegistrantReferral(submissionId).then((r) => { if (alive) setReferral(r); });
    return () => { alive = false; };
  }, [submissionId]);

  if (!referral) return null;
  const shown = referral.shortUrl.replace(/^https:\/\//, '');

  async function copy() {
    try {
      await navigator.clipboard?.writeText(referral!.shortUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      /* the link stays visible to copy by hand */
    }
  }

  return (
    <div className="card referral-block" style={{ marginTop: 24, padding: '24px 28px', textAlign: 'center' }}>
      <h2 style={{ marginTop: 0, fontSize: '1.2rem' }}>Bring a colleague</h2>
      <p className="muted" style={{ lineHeight: 1.55, margin: '0 0 14px' }}>
        Share your own link. Everyone who registers through it is counted to you.
        {referral.nextTreat && (
          <> Reach {referral.nextTreat.atReferrals} and you get <strong>{referral.nextTreat.label}</strong>.</>
        )}
      </p>
      <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap', alignItems: 'center' }}>
        <code style={{ fontSize: '1rem', padding: '8px 12px', border: '1px solid var(--color-border, #e5e7eb)', borderRadius: 8, wordBreak: 'break-all' }}>{shown}</code>
        <button type="button" className="btn btn-secondary" onClick={copy}>{copied ? 'Copied' : 'Copy link'}</button>
      </div>
      <p style={{ margin: '14px 0 0', fontSize: '0.95rem' }}>
        {referral.count === 0
          ? 'Nobody has registered through your link yet.'
          : `${referral.count} ${referral.count === 1 ? 'colleague has' : 'colleagues have'} registered through your link.`}
        {referral.nextTreat && referral.count > 0 && ` ${referral.nextTreat.more} more to go.`}
      </p>
      {referral.sharePageUrl && (
        <p style={{ margin: '12px 0 0', fontSize: '0.95rem' }}>
          <a href={referral.sharePageUrl}>Share it in one click on LinkedIn, Facebook, X, WhatsApp or email</a>
        </p>
      )}
    </div>
  );
}
